import * as React from "react";

import { UseFormReturn } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@ps-design-system/components/ExpandableContent";
import { Text } from "@ps-design-system/components/Text";
import CheckCircleFilledIcon from "@ps-design-system/icons/CheckCircleFilled/CheckCircleFilled20";

import {
  ActivateListingForm,
  ActivateListingFormValues,
} from "../ActivateListingForm";
import { USE_TYPE_SLUG } from "../ActivateListingsPage.consts";
import type {
  AmenityOption,
  ListingUseTypeConfig,
} from "../ActivateListingsPage.types";

import {
  BULLETS_BY_USE_TYPE,
  HEADER_BY_USE_TYPE,
} from "./ActivateListingCard.consts";

type ActivateListingCardProps = {
  config: ListingUseTypeConfig;
  amenityOptions: AmenityOption[];
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<ActivateListingFormValues>;
  spaceCountry: string;
  isInstantBookEligible: boolean;
};

const ActivateListingCard = ({
  config,
  amenityOptions,
  onOpenChange,
  form,
  spaceCountry,
  isInstantBookEligible,
}: ActivateListingCardProps) => {
  const [isEnabled, setIsEnabled] = React.useState(false);
  const slug = USE_TYPE_SLUG[config.useType];

  return (
    <div
      className="tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white"
      data-testid={`activate-listing-card-${slug}`}
    >
      <div className="tw-p-16">
        <Text typeface="headline-3-b tw-mb-8">
          <FormattedMessage {...HEADER_BY_USE_TYPE[config.useType]} />
        </Text>
        <ul
          className="tw-body-2 tw-list-disc tw-list-inside tw-mb-0"
          data-testid={`activate-listing-card-${slug}-bullets`}
        >
          {BULLETS_BY_USE_TYPE[config.useType].map((item) => (
            <li key={item.id}>
              <FormattedMessage {...item} />
            </li>
          ))}
          <li>
            <FormattedMessage
              id="hosting.activate_listings.item_more"
              defaultMessage="More"
            />
          </li>
        </ul>
      </div>
      <div className="tw-pb-16">
        <Collapsible
          onOpenChange={(open: boolean) => {
            setIsEnabled(open);
            onOpenChange(open);
          }}
        >
          <div className="tw-px-16">
            <CollapsibleTrigger asChild>
              <Button
                fullWidth={true}
                appearance={isEnabled ? "black" : "white"}
                variant={isEnabled ? "default" : "outline-1"}
                prefix={isEnabled ? <CheckCircleFilledIcon /> : null}
                testId={`activate-listing-card-${slug}-toggle-button`}
              >
                {isEnabled ? (
                  <FormattedMessage
                    id="hosting.activate_listings.enabled"
                    defaultMessage="Enabled"
                  />
                ) : (
                  <FormattedMessage
                    id="hosting.activate_listings.activate"
                    defaultMessage="Activate"
                  />
                )}
              </Button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent>
            <ActivateListingForm
              amenityOptions={amenityOptions}
              config={config}
              spaceCountry={spaceCountry}
              form={form}
              isInstantBookEligible={isInstantBookEligible}
            />
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};

export { ActivateListingCard };
